import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import {
  filterByNameAndTitle,
  nameSearchQueryAtomJ,
  peopleByCompanyStateJ,
  showHiddenPeopleAtomJ,
  titleSearchQueryAtomJ,
} from '../../../services/state/PeopleByCompanyStateJ';
import { selectedCompanyIdProfileAtom } from '../state/UIStateJ';
import { getBoardMembersForCompany, getEmployeesForCompany } from '../utils/peopleHelpers';

export const usePeopleData = () => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const showHidden = useAtomValue(showHiddenPeopleAtomJ);
  const nameQuery = useAtomValue(nameSearchQueryAtomJ);
  const titleQuery = useAtomValue(titleSearchQueryAtomJ);
  const people = useAtomValue(peopleByCompanyStateJ(companyId));

  // allPeople returns ALL people, visibility-filtered based on showHidden
  // This maintains the original semantics where allPeople.length is total visible count
  const allPeople = useMemo(() => {
    if (!people) {
      return null;
    }
    const hiddenPeople = people.filter((p) => p.hidden);
    const visiblePeople = people.filter((p) => !p.hidden);
    return showHidden ? [...visiblePeople, ...hiddenPeople] : visiblePeople;
  }, [people, showHidden]);

  // Get employees and board members before applying search filters
  const allEmployees = useMemo(() => {
    if (!allPeople) {
      return null;
    }
    return getEmployeesForCompany(allPeople);
  }, [allPeople]);

  const allBoardMembers = useMemo(() => {
    if (!allPeople) {
      return null;
    }
    return getBoardMembersForCompany(allPeople);
  }, [allPeople]);

  // Apply name/title search filtering to get final filtered results
  const filteredEmployees = useMemo(() => {
    return filterByNameAndTitle(allEmployees, nameQuery, titleQuery, companyId);
  }, [allEmployees, nameQuery, titleQuery, companyId]);

  const filteredBoardMembers = useMemo(() => {
    return filterByNameAndTitle(allBoardMembers, nameQuery, titleQuery, companyId);
  }, [allBoardMembers, nameQuery, titleQuery, companyId]);

  const hiddenCount = people?.filter((p) => p.hidden).length ?? 0;

  return {
    allPeople,
    filteredEmployees,
    filteredBoardMembers,
    hiddenCount,
  };
};
